import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mapApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { AdminCell, MapTemplate, CellTypeAdmin, MapBackgroundTheme, MapWeatherConfig, ClimbConnectionType, CameraRotationDeg, NorthSide } from '../../../types/adminTypes';
import { MAP_DEFAULTS, BACKGROUND_THEME_OPTIONS, WEATHER_TYPE_OPTIONS, CAMERA_ROTATION_OPTIONS, NORTH_SIDES } from '../../../types/adminTypes';
import { CombatGrid } from '../../../components/CombatGrid/CombatGrid';

// ── Constants ─────────────────────────────────────────────────────────────────
// 'High_Ground' foi removido do pincel: redundante com a ferramenta de
// Elevação (Níveis -2 a +2), que já controla a altura de forma exclusiva —
// manter os dois causava conflito de dados (célula com type='High_Ground'
// mas elevation diferente de 1). Mapas antigos com essa célula continuam
// renderizando corretamente (getElevation/cellElevation tratam o legado).
// 'Ramp' também saiu do pincel: a célula-rampa antiga deixava vãos na
// geometria (o céu aparecia pelas laterais). Subir/descer agora é só pela
// Via de Subida (Rampa/Escada/Corda); mapas antigos são convertidos ao abrir.
const CELL_TYPES: CellTypeAdmin[] = ['Walkable', 'Full_Obstacle', 'Vision_Obstacle', 'Tree', 'Mountain', 'Rock', 'Lake'];

// Pincéis de Terreno = tipos de célula + 'Dirt_Path' (Chão Batido). Este
// último NÃO é um tipo novo: pinta `type: 'Walkable'` + `floor: 'dirt'`, então
// movimento/LOS no backend continuam tratando a trilha como caminhável comum.
type TerrainBrush = CellTypeAdmin | 'Dirt_Path';
const TERRAIN_BRUSHES: TerrainBrush[] = ['Walkable', 'Dirt_Path', ...CELL_TYPES.slice(1)];
const DIRT_COLOR = '#7a5a36';

/**
 * Tipos que aceitam Nível (altura): terreno caminhável (inclui Chão Batido,
 * que é Walkable + floor) e a Rampa legada. Lago, obstáculos, árvore e pedra
 * ficam sempre no nível 0 — elevar um lago "afundava" a água entre paredões.
 * (Montanha tem o próprio nível 2, aplicado pelo pincel dela.)
 */
function acceptsElevation(cell: AdminCell): boolean {
  return cell.type === 'Walkable';
}

function applyTerrainBrush(cell: AdminCell, brush: TerrainBrush): AdminCell {
  const { floor: _floor, ...rest } = cell;
  if (brush === 'Dirt_Path') return { ...rest, type: 'Walkable', floor: 'dirt' };
  if (brush === 'Mountain')  return { ...rest, type: 'Mountain', elevation: 2 };
  const painted: AdminCell = { ...rest, type: brush };
  // Tipo que não aceita nível: volta ao chão e perde a Via de Subida.
  return acceptsElevation(painted) ? painted : { ...painted, elevation: 0, climb_connection: null };
}

/**
 * Pinta a célula (x, y) em `next` (já copiado) e, se ela perdeu a Via de
 * Subida, remove também a ponta na vizinha que apontava para ela.
 */
function paintCellInto(next: AdminCell[][], y: number, x: number, painted: AdminCell) {
  const before = next[y][x].climb_connection;
  next[y][x] = painted;
  if (before && !painted.climb_connection) {
    const { targetX: bx, targetY: by } = before;
    const partner = next[by]?.[bx];
    if (partner?.climb_connection?.targetX === x && partner.climb_connection.targetY === y) {
      next[by] = next[by].slice();
      next[by][bx] = { ...partner, climb_connection: null };
    }
  }
}

// ── Map randomiser ─────────────────────────────────────────────────────────────
const _MAP_ADJS  = ['Ancient', 'Dark', 'Frozen', 'Burning', 'Sunken', 'Cursed', 'Sacred', 'Haunted', 'Verdant', 'Barren', 'Misty', 'Forsaken'] as const;
const _MAP_NOUNS = ['Ruins', 'Dungeon', 'Forest', 'Caves', 'Crypt', 'Vault', 'Fortress', 'Marsh', 'Temple', 'Hollow', 'Depths', 'Labyrinth'] as const;

interface RandomMapResult {
  name:   string;
  width:  number;
  height: number;
  cells:  AdminCell[][];
}

function generateCaveGrid(width: number, height: number): CellTypeAdmin[][] {
  const pick = <T,>(arr: readonly T[]): T  => arr[Math.floor(Math.random() * arr.length)];

  // Step 1 — random fill (borders = wall, interior ~38% walls)
  let grid: CellTypeAdmin[][] = Array.from({ length: height }, (_, y) =>
    Array.from({ length: width }, (_, x) =>
      x === 0 || x === width - 1 || y === 0 || y === height - 1
        ? 'Full_Obstacle'
        : Math.random() < 0.38 ? 'Full_Obstacle' : 'Walkable'
    )
  );

  // Step 2 — 4 smoothing passes
  for (let pass = 0; pass < 4; pass++) {
    grid = grid.map((row, y) =>
      row.map((_, x) => {
        if (x === 0 || x === width - 1 || y === 0 || y === height - 1) return 'Full_Obstacle';
        let walls = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            if (grid[y + dy]?.[x + dx] === 'Full_Obstacle') walls++;
          }
        }
        // Classic cave rule: ≥5 wall neighbours → wall, ≤3 → floor
        return walls >= 5 ? 'Full_Obstacle' : (walls <= 3 ? 'Walkable' : grid[y][x]);
      })
    );
  }

  // Step 3 — Decorate with new tiles (Tree, Mountain, Rock, Lake)
  const solidObstacles: CellTypeAdmin[] = ['Mountain', 'Rock', 'Tree', 'Full_Obstacle'];
  grid = grid.map((row, y) =>
    row.map((cell, x): CellTypeAdmin => {
      // 1) Keep the outermost borders as strict Full_Obstacle
      if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
        return 'Full_Obstacle';
      }

      // 2) Decorate interior obstacles
      if (cell === 'Full_Obstacle') {
        // 70% chance to be a thematic obstacle instead of generic wall
        return Math.random() < 0.7 ? pick(solidObstacles) : 'Full_Obstacle';
      }

      // 3) Decorate walkable area (sparse)
      if (cell === 'Walkable') {
        const r = Math.random();
        // Lower probabilities so the map doesn't get cluttered
        if (r < 0.02) return 'Lake';            // 2% chance
        if (r < 0.06) return 'Vision_Obstacle'; // 4% chance (0.02 to 0.06)
        if (r < 0.10) return 'Tree';            // 4% chance (0.06 to 0.10)
      }
      return cell;
    })
  );

  return grid;
}

function randomizeMapData(): RandomMapResult {
  const ri   = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
  const pick = <T,>(arr: readonly T[]): T  => arr[Math.floor(Math.random() * arr.length)];

  const name   = `${pick(_MAP_ADJS)} ${pick(_MAP_NOUNS)}`;
  const width  = ri(8, 22);
  const height = ri(12, 28);

  const grid = generateCaveGrid(width, height);

  const cells: AdminCell[][] = grid.map((row, y) =>
    row.map((type, x) => ({ x, y, type }))
  );

  return { name, width, height, cells };
}

const CELL_COLORS: Record<CellTypeAdmin, string> = {
  Walkable:        '#3a5e3a',
  Full_Obstacle:   '#222222',
  Vision_Obstacle: '#555533',
  Tree:            '#1f4d1f',
  Mountain:        '#4b4b4b',
  Rock:            '#6a6a6a',
  Lake:            '#2a5a8a',
  High_Ground:     '#5a7a5a',
  Ramp:            '#8b5a2b',
};
const BRUSH_COLORS: Record<TerrainBrush, string> = { ...CELL_COLORS, Dirt_Path: DIRT_COLOR };

// ── Elevação de Terreno ──────────────────────────────────────────────────────
// Nível 0 = Chão Padrão · +1/+2 = Rampa/Planalto até o Cume da Montanha
// · -1/-2 = Buraco/Vala (profundidade subterrânea).
const ELEVATION_LEVELS = [-2, -1, 0, 1, 2] as const;
type ElevationLevel = typeof ELEVATION_LEVELS[number];
const ELEVATION_ICONS: Record<ElevationLevel, string> = { '-2': '▽', '-1': '▿', 0: '▁', 1: '▄', 2: '█' };
// Cor de destaque (borda/ícone) do nível — usada nas amostras do pincel.
const ELEVATION_SHADE: Record<ElevationLevel, string> = {
  '-2': 'rgba(120,200,255,0.85)', '-1': 'rgba(90,150,190,0.85)',
  0: 'transparent',
  1: 'rgba(139,90,43,0.85)', 2: 'rgba(255,255,255,0.85)',
};
// Preenchimento SÓLIDO do piso elevado/rebaixado na prévia 2D — corrige o bug
// do "buraco oco" (o antigo inset box-shadow só desenhava um anel, deixando o
// centro da célula vazio/transparente).
const ELEVATION_FILL: Record<ElevationLevel, string | undefined> = {
  '-2': '#0f0c08', '-1': '#241d15',
  0: undefined,
  1: '#5a4326', 2: '#8a8168',
};

// ── Via de Subida (Ponto A → Ponto B) ────────────────────────────────────────
// Conecta duas células vizinhas de níveis diferentes, liberando passagem e
// trocando o muro vertical pelo visual escolhido (Rampa/Escada/Corda).
const CLIMB_TYPES: ClimbConnectionType[] = ['RAMP', 'STAIRS', 'ROPE'];
const CLIMB_ICONS: Record<ClimbConnectionType, string> = { RAMP: '⛰', STAIRS: '🪜', ROPE: '🪢' };

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildEmptyGrid(width: number, height: number): AdminCell[][] {
  return Array.from({ length: height }, (_, y) =>
    Array.from({ length: width }, (_, x) => ({ x, y, type: 'Walkable' as CellTypeAdmin, elevation: 0 }))
  );
}

function resizeGrid(
  old: AdminCell[][],
  newWidth: number,
  newHeight: number
): AdminCell[][] {
  return Array.from({ length: newHeight }, (_, y) =>
    Array.from({ length: newWidth }, (_, x) => ({
      x,
      y,
      type: (old[y]?.[x]?.type ?? 'Walkable') as CellTypeAdmin,
      elevation: old[y]?.[x]?.elevation ?? 0,
      climb_connection: old[y]?.[x]?.climb_connection ?? null,
      ...(old[y]?.[x]?.floor ? { floor: old[y][x].floor } : {}),
    }))
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function MapEditorPage() {
  const { id }   = useParams<{ id: string }>();
  const isEdit   = Boolean(id);
  const navigate = useNavigate();
  const { t }    = useTranslation();

  const [name,       setName]       = useState('');
  const [description,setDescription]= useState('');
  const [width,      setWidth]      = useState(MAP_DEFAULTS.width);
  const [height,     setHeight]     = useState(MAP_DEFAULTS.height);
  const [cells,      setCells]      = useState<AdminCell[][]>(() => buildEmptyGrid(MAP_DEFAULTS.width, MAP_DEFAULTS.height));
  const [brush,      setBrush]      = useState<TerrainBrush>('Walkable');
  const [painting,   setPainting]   = useState(false);
  const [loading,    setLoading]    = useState(isEdit);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [backgroundTheme, setBackgroundTheme] = useState<MapBackgroundTheme>(MAP_DEFAULTS.background_theme ?? 'medieval_mountains_day');
  const [weatherType, setWeatherType] = useState<MapWeatherConfig>(MAP_DEFAULTS.weather_type ?? 'SUNNY');
  // O Preview 3D não entende 'AUTO' (isso só existe como config do template
  // — quem resolve pra um clima concreto é o backend ao iniciar a batalha,
  // ver TurnMachineService::resolveWeatherType). Com 'AUTO' selecionado, o
  // preview mostra Ensolarado como uma amostra fixa qualquer.
  const previewWeatherType = weatherType === 'AUTO' ? 'SUNNY' : weatherType;
  const [backgroundEnabled, setBackgroundEnabled] = useState<boolean>(MAP_DEFAULTS.background_enabled ?? true);
  const [cameraRotation, setCameraRotation] = useState<CameraRotationDeg>(MAP_DEFAULTS.camera_rotation ?? 120);
  const [north, setNorth] = useState<NorthSide>(MAP_DEFAULTS.north ?? 0);
  // Guia de Terrenos: em vez do card fixo antigo, um ícone de ajuda "?" no
  // canto do painel Modo de Pincel revela a legenda num popover ao clicar.
  const [showGuide, setShowGuide] = useState(false);

  // ── Ferramenta de Altura/Elevação ──────────────────────────────────────────
  // 'terrain'   → o pincel pinta o tipo de célula (comportamento original).
  // 'elevation' → o pincel pinta o Nível de Terreno (0/1/2) sem alterar o tipo.
  // 'climb'     → ferramenta de Via de Subida Ponto A → Ponto B (2 cliques).
  const [brushMode,      setBrushMode]      = useState<'terrain' | 'elevation' | 'climb'>('terrain');
  const [elevationLevel, setElevationLevel] = useState<ElevationLevel>(1);
  const [climbType,      setClimbType]      = useState<ClimbConnectionType>('RAMP');
  // Ponto A já selecionado, aguardando o clique no Ponto B (célula vizinha).
  const [climbPendingA,  setClimbPendingA]  = useState<{ x: number; y: number } | null>(null);

  // Ref to keep current brush in mouse-move handler without re-binding
  const brushRef = useRef(brush);
  useEffect(() => { brushRef.current = brush; }, [brush]);
  const brushModeRef = useRef(brushMode);
  useEffect(() => { brushModeRef.current = brushMode; }, [brushMode]);
  const elevationLevelRef = useRef(elevationLevel);
  useEffect(() => { elevationLevelRef.current = elevationLevel; }, [elevationLevel]);
  const climbTypeRef = useRef(climbType);
  useEffect(() => { climbTypeRef.current = climbType; }, [climbType]);

  // ── Load map ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    mapApi.get(id!)
      .then((m: MapTemplate) => {
        setName(m.name);
        setDescription(m.description);
        setWidth(m.width);
        setHeight(m.height);
        setBackgroundTheme(m.background_theme ?? 'medieval_mountains_day');
        setWeatherType(m.weather_type ?? 'SUNNY');
        setBackgroundEnabled(m.background_enabled ?? true);
        setCameraRotation(m.camera_rotation ?? 120);
        setNorth(m.north ?? 0);
        // Normalise – server may return flat array or 2-D array
        const grid: AdminCell[][] = buildEmptyGrid(m.width, m.height);
        const src = m.cells;
        if (Array.isArray(src)) {
          src.forEach((rowOrCell) => {
            if (Array.isArray(rowOrCell)) {
              // 2-D
              (rowOrCell as AdminCell[]).forEach((c) => {
                if (grid[c.y]?.[c.x]) grid[c.y][c.x] = c;
              });
            } else {
              // flat
              const c = rowOrCell as AdminCell;
              if (grid[c.y]?.[c.x]) grid[c.y][c.x] = c;
            }
          });
        }
        // Dados antigos: lago/árvore/pedra/névoa com nível ou Via de Subida
        // (ex.: lago elevado "afundando" entre paredões) voltam ao chão; vias
        // órfãs (a outra ponta não aponta de volta) são removidas. Corrige o
        // mapa no próximo Salvar. Full_Obstacle/Montanha elevados são legado
        // intencional (platô sólido) e ficam como estão.
        // Rampa antiga (tipo 'Ramp'): vira terreno caminhável no mesmo nível
        // + Via de Subida tipo RAMP até a vizinha ortogonal de outro nível
        // (preferindo a mais baixa), mantendo a passagem que ela oferecia.
        for (const row of grid) for (const c of row) {
          if (c.type !== 'Ramp') continue;
          const elev = c.elevation ?? 0;
          const nbs = [[0, 1], [1, 0], [0, -1], [-1, 0]]
            .map(([dx, dy]) => grid[c.y + dy]?.[c.x + dx])
            .filter((n): n is AdminCell => !!n && n.type !== 'Ramp' && (n.type === 'Walkable') && (n.elevation ?? 0) !== elev && !n.climb_connection)
            .sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
          const target = nbs[0];
          grid[c.y][c.x] = { ...c, type: 'Walkable', climb_connection: target ? { targetX: target.x, targetY: target.y, type: 'RAMP' } : null };
          if (target) grid[target.y][target.x] = { ...target, climb_connection: { targetX: c.x, targetY: c.y, type: 'RAMP' } };
        }
        const GROUND_ONLY = new Set<CellTypeAdmin>(['Lake', 'Tree', 'Rock', 'Vision_Obstacle']);
        for (const row of grid) for (const c of row) {
          if (GROUND_ONLY.has(c.type) && ((c.elevation ?? 0) !== 0 || c.climb_connection)) {
            grid[c.y][c.x] = { ...c, elevation: 0, climb_connection: null };
          }
        }
        for (const row of grid) for (const c of row) {
          const link = c.climb_connection;
          if (!link) continue;
          const back = grid[link.targetY]?.[link.targetX]?.climb_connection;
          if (!back || back.targetX !== c.x || back.targetY !== c.y) {
            grid[c.y][c.x] = { ...c, climb_connection: null };
          }
        }
        setCells(grid);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // ── Dimensions: resize grid on change ────────────────────────────────────
  const applyDimensions = () => {
    const w = Math.min(30, Math.max(5, width));
    const h = Math.min(40, Math.max(5, height));
    setWidth(w);
    setHeight(h);
    setCells((prev) => resizeGrid(prev, w, h));
  };

  // ── Painting ──────────────────────────────────────────────────────────────
  // Pintar o bloco "Montanha" no pincel de Terreno aplica automaticamente a
  // elevação de Nível 2 (Cume) — o admin não precisa trocar para o pincel de
  // Elevação separadamente só para ter uma montanha sólida e caminhável.
  const paint = useCallback((y: number, x: number) => {
    setCells((prev) => {
      const next = prev.map((row) => row.slice());   // shallow copy rows
      if (next[y]?.[x]) {
        next[y] = next[y].slice();
        if (brushModeRef.current === 'elevation') {
          // Nível só em terreno caminhável — nos demais o pincel é ignorado.
          if (acceptsElevation(next[y][x])) next[y][x] = { ...next[y][x], elevation: elevationLevelRef.current };
        } else {
          paintCellInto(next, y, x, applyTerrainBrush(next[y][x], brushRef.current));
        }
      }
      return next;
    });
  }, []);

  // ── Ferramenta de Via de Subida (Ponto A → Ponto B) ─────────────────────────
  // Fluxo de 2 cliques: 1º clique marca a célula A; 2º clique numa vizinha
  // ortogonal de nível diferente cria (ou remove, se já existir) a conexão —
  // gravada simetricamente nas duas pontas para o pathfinding/render
  // enxergarem a via de qualquer um dos lados.
  const onClimbCellClick = useCallback((y: number, x: number) => {
    setClimbPendingA((prevA) => {
      if (!prevA) return { x, y };
      if (prevA.x === x && prevA.y === y) return null; // clicou de novo em A: cancela

      const dx = Math.abs(prevA.x - x);
      const dy = Math.abs(prevA.y - y);
      const isNeighbor = (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
      if (!isNeighbor) {
        // B não é vizinha de A — recomeça a seleção a partir desta célula.
        return { x, y };
      }

      setCells((prevCells) => {
        const cellA = prevCells[prevA.y]?.[prevA.x];
        const cellB = prevCells[y]?.[x];
        if (!cellA || !cellB) return prevCells;
        if (!acceptsElevation(cellA) || !acceptsElevation(cellB)) return prevCells; // via só entre terreno caminhável
        if ((cellA.elevation ?? 0) === (cellB.elevation ?? 0)) return prevCells; // mesmo nível — nada a conectar

        const alreadyConnected =
          cellA.climb_connection?.targetX === x && cellA.climb_connection?.targetY === y;

        const next = prevCells.map((row) => row.slice());
        next[prevA.y] = next[prevA.y].slice();
        next[y]       = next[y].slice();

        next[prevA.y][prevA.x] = {
          ...cellA,
          climb_connection: alreadyConnected ? null : { targetX: x, targetY: y, type: climbTypeRef.current },
        };
        next[y][x] = {
          ...cellB,
          climb_connection: alreadyConnected ? null : { targetX: prevA.x, targetY: prevA.y, type: climbTypeRef.current },
        };
        return next;
      });

      return null; // seleção concluída — próximo clique inicia um novo A
    });
  }, []);

  const onCellDown  = (y: number, x: number) => {
    if (brushModeRef.current === 'climb') { onClimbCellClick(y, x); return; }
    setPainting(true);
    paint(y, x);
  };
  const onCellEnter = (y: number, x: number) => { if (painting) paint(y, x); };
  const onMouseUp   = () => setPainting(false);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError(t('mapEditor.nameRequired')); return; }

    setSaving(true);
    setError('');
    const payload: MapTemplate = {
      name,
      description,
      width,
      height,
      cells,
      background_theme: backgroundTheme,
      weather_type: weatherType,
      background_enabled: backgroundEnabled,
      camera_rotation: cameraRotation,
      north,
    };

    try {
      if (isEdit) {
        await mapApi.update(id!, payload);
      } else {
        await mapApi.create(payload);
      }
      navigate('/admin/maps');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // ── Fill all ──────────────────────────────────────────────────────────────
  const fillAll = () => {
    setCells((prev) =>
      prev.map((row) => row.map((c) => (
        brushMode === 'elevation'
          ? (acceptsElevation(c) ? { ...c, elevation: elevationLevel } : c)
          : applyTerrainBrush(c, brush)
      )))
    );
  };

  // ── Randomise everything (name + dimensions + cells) ─────────────────────
  const randomizeFull = () => {
    const r = randomizeMapData();
    setName(r.name);
    setWidth(r.width);
    setHeight(r.height);
    setCells(r.cells);
  };

  // ── Randomise only the cells (keep name & dimensions, regenerate layout) ──
  const randomizeCells = () => {
    const w = Math.min(30, Math.max(5, width));
    const h = Math.min(40, Math.max(5, height));

    const grid = generateCaveGrid(w, h);
    setCells(grid.map((row, y) => row.map((type, x) => ({ x, y, type }))));
  };

  if (loading) return (
    <>
      <div className="adminHeader"><h1>{t('common.loading')}</h1></div>
      <div className="adminContent"><div className="spinner" /></div>
    </>
  );

  return (
    <>
      <div className="adminHeader">
        <h1>{isEdit ? `✏ Edit: ${name}` : t('mapEditor.titleNew')}</h1>
        <button
          type="button"
          className="btn btnSecondary"
          onClick={randomizeFull}
          title={t('common.randomize')}
        >
          {t('common.randomize')}
        </button>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        <form onSubmit={(e) => void handleSubmit(e)} className="adminForm">

          {/* ── Meta ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('mapEditor.meta')}</div>
            <div className="formGrid">
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('mapEditor.name')}</label>
                <input
                  type="text"
                  className="formInput"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('mapEditor.namePlaceholder')}
                  required
                />
              </div>
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('mapEditor.description')}</label>
                <textarea
                  className="formTextarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('mapEditor.descPlaceholder')}
                />
              </div>

              {/* Plano de Fundo Medieval Dinâmico (Parallax Ambiance) */}
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('mapEditor.backgroundTheme')}</label>
                <select
                  className="formInput"
                  value={backgroundTheme}
                  onChange={(e) => setBackgroundTheme(e.target.value as MapBackgroundTheme)}
                >
                  {BACKGROUND_THEME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey as Parameters<typeof t>[0])}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sistema de Iluminação e Clima Variável */}
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('mapEditor.weatherType')}</label>
                <select
                  className="formInput"
                  value={weatherType}
                  onChange={(e) => setWeatherType(e.target.value as MapWeatherConfig)}
                >
                  {WEATHER_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey as Parameters<typeof t>[0])}
                    </option>
                  ))}
                </select>
              </div>

              {/* Limite de Rotação da Câmera (arrasto do botão direito) */}
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('mapEditor.cameraRotation')}</label>
                <select
                  className="formInput"
                  value={cameraRotation}
                  onChange={(e) => setCameraRotation(Number(e.target.value) as CameraRotationDeg)}
                >
                  {CAMERA_ROTATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey as Parameters<typeof t>[0])}
                    </option>
                  ))}
                </select>
              </div>

              {/* Norte da bússola: qual lado do mapa (visão de cima) é o Norte */}
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('compass.northSide')}</label>
                <select
                  className="formInput"
                  value={north}
                  onChange={(e) => setNorth(Number(e.target.value) as NorthSide)}
                >
                  {NORTH_SIDES.map((side) => (
                    <option key={side} value={side}>{t(`compass.side.${side}`)}</option>
                  ))}
                </select>
              </div>

              {/* Toggle "Exibir Fundo (Céu)" */}
              <div className="formGroup formGroupFull">
                <label className="formLabel" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={backgroundEnabled}
                    onChange={(e) => setBackgroundEnabled(e.target.checked)}
                  />
                  {t('mapEditor.backgroundEnabled')}
                </label>
              </div>

              {/* Dimensions */}
              <div className="formGroup">
                <label className="formLabel">{t('mapEditor.width')}</label>
                <input
                  type="number"
                  className="formInput"
                  value={width}
                  min={5} max={30}
                  onChange={(e) => setWidth(parseInt(e.target.value, 10) || 5)}
                />
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('mapEditor.height')}</label>
                <input
                  type="number"
                  className="formInput"
                  value={height}
                  min={5} max={40}
                  onChange={(e) => setHeight(parseInt(e.target.value, 10) || 5)}
                />
              </div>
              <div className="formGroup" style={{ justifyContent: 'flex-end' }}>
                <label className="formLabel" style={{ visibility: 'hidden' }}>_</label>
                <button type="button" className="btn btnSecondary" onClick={applyDimensions}>
                  {t('mapEditor.applyDimensions')}
                </button>
              </div>
            </div>
          </div>

          {/* ── Grid Editor ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('mapEditor.cellEditor')}</div>
            <div style={{ padding: '14px 18px' }}>
              <div className="mapEditorLayout" onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>

                {/* Editor sidebar */}
                <div className="mapEditorSidebar">
                  {/* Modo de Pincel: Terreno vs Nível de Terreno / Elevação */}
                  <div className="adminCard" style={{ marginBottom: 0, position: 'relative' }}>
                    <div className="adminCardHeader" style={{ fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {t('mapEditor.brushMode')}
                      <button
                        type="button"
                        title={t('mapEditor.terrainGuide')}
                        onClick={() => setShowGuide((v) => !v)}
                        style={{
                          width: 18, height: 18, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.3)',
                          background: showGuide ? 'var(--accent, #e8c07a)' : 'rgba(255,255,255,0.08)',
                          color: showGuide ? '#1a1a1a' : 'var(--text-dim)',
                          fontSize: 11, fontWeight: 700, lineHeight: 1, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                        }}
                      >
                        ?
                      </button>
                    </div>
                    {showGuide && (
                      <div
                        className="mapEditorGuidePopover"
                        style={{
                          position: 'absolute', top: 4, left: '100%', marginLeft: 10, zIndex: 20,
                          width: 260, maxHeight: 360, overflowY: 'auto',
                          background: 'var(--surface, #1c1c1c)', border: '1px solid var(--border, #333)',
                          borderRadius: 8, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                          display: 'flex', flexDirection: 'column', gap: 10,
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {t('mapEditor.terrainGuide')}
                        </div>
                        {TERRAIN_BRUSHES.map((ct) => (
                          <div key={ct} style={{ display: 'flex', gap: 8 }}>
                            <span style={{ minWidth: 14, height: 14, borderRadius: 2, background: BRUSH_COLORS[ct], border: '1px solid rgba(255,255,255,0.1)', marginTop: 2 }} />
                            <div style={{ fontSize: 11, lineHeight: 1.4 }}>
                              <strong style={{ color: 'var(--text)', display: 'block' }}>{t(`cell.${ct}` as Parameters<typeof t>[0])}</strong>
                              <span style={{ color: 'var(--text-dim)' }}>{t(`cellDesc.${ct}` as Parameters<typeof t>[0])}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, padding: '10px 12px' }}>
                      <button
                        type="button"
                        className={`btn ${brushMode === 'terrain' ? 'btnPrimary' : 'btnSecondary'}`}
                        style={{ flex: 1, fontSize: 11 }}
                        onClick={() => setBrushMode('terrain')}
                      >
                        {t('mapEditor.terrainBrush')}
                      </button>
                      <button
                        type="button"
                        className={`btn ${brushMode === 'elevation' ? 'btnPrimary' : 'btnSecondary'}`}
                        style={{ flex: 1, fontSize: 11 }}
                        onClick={() => setBrushMode('elevation')}
                      >
                        {t('mapEditor.elevationBrush')}
                      </button>
                      <button
                        type="button"
                        className={`btn ${brushMode === 'climb' ? 'btnPrimary' : 'btnSecondary'}`}
                        style={{ flex: 1, fontSize: 11 }}
                        onClick={() => { setBrushMode('climb'); setClimbPendingA(null); }}
                      >
                        {t('mapEditor.climbBrush')}
                      </button>
                    </div>
                  </div>

                  {brushMode === 'terrain' ? (
                    <div className="adminCard" style={{ marginBottom: 0 }}>
                      <div className="adminCardHeader" style={{ fontSize: 11 }}>{t('mapEditor.brush')}</div>
                      <div className="brushSelector" style={{ padding: '10px 12px' }}>
                        {TERRAIN_BRUSHES.map((ct) => (
                          <button
                            key={ct}
                            type="button"
                            className={`brushBtn${brush === ct ? ' selected' : ''}`}
                            onClick={() => setBrush(ct)}
                          >
                            <span className="brushSwatch" style={{ background: BRUSH_COLORS[ct] }} />
                            {t(`cell.${ct}` as Parameters<typeof t>[0])}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : brushMode === 'elevation' ? (
                    <div className="adminCard" style={{ marginBottom: 0 }}>
                      <div className="adminCardHeader" style={{ fontSize: 11 }}>{t('mapEditor.elevationLevel')}</div>
                      <div className="brushSelector" style={{ padding: '10px 12px' }}>
                        {ELEVATION_LEVELS.map((lvl) => (
                          <button
                            key={lvl}
                            type="button"
                            className={`brushBtn${elevationLevel === lvl ? ' selected' : ''}`}
                            onClick={() => setElevationLevel(lvl)}
                          >
                            <span className="brushSwatch" style={{ background: ELEVATION_SHADE[lvl], border: '1px solid rgba(255,255,255,0.25)' }}>
                              {ELEVATION_ICONS[lvl]}
                            </span>
                            {t(`elevationLevel.${lvl}` as Parameters<typeof t>[0])}
                          </button>
                        ))}
                      </div>
                      <div style={{ padding: '0 12px 10px', fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.4 }}>
                        {t('mapEditor.elevationHint')}
                      </div>
                    </div>
                  ) : (
                    <div className="adminCard" style={{ marginBottom: 0 }}>
                      <div className="adminCardHeader" style={{ fontSize: 11 }}>{t('mapEditor.climbType')}</div>
                      <div className="brushSelector" style={{ padding: '10px 12px' }}>
                        {CLIMB_TYPES.map((ctype) => (
                          <button
                            key={ctype}
                            type="button"
                            className={`brushBtn${climbType === ctype ? ' selected' : ''}`}
                            onClick={() => setClimbType(ctype)}
                          >
                            <span className="brushSwatch" style={{ background: '#3c362c', border: '1px solid rgba(255,255,255,0.25)' }}>
                              {CLIMB_ICONS[ctype]}
                            </span>
                            {t(`climbType.${ctype}` as Parameters<typeof t>[0])}
                          </button>
                        ))}
                      </div>
                      <div style={{ padding: '0 12px 6px', fontSize: 11, fontWeight: 600, color: climbPendingA ? '#e8c07a' : 'var(--text-dim)' }}>
                        {climbPendingA
                          ? `${t('mapEditor.climbSelectB')} (A: ${climbPendingA.x}, ${climbPendingA.y})`
                          : t('mapEditor.climbSelectA')}
                      </div>
                      <div style={{ padding: '0 12px 10px', fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.4 }}>
                        {t('mapEditor.climbHint')}
                      </div>
                    </div>
                  )}

                  <button type="button" className="btn btnSecondary" style={{ width: '100%' }} onClick={fillAll}>
                    {t('mapEditor.fillAll')}
                  </button>

                  <button type="button" className="btn btnSecondary" style={{ width: '100%' }} onClick={randomizeCells}>
                    {t('mapEditor.randomizeCells')}
                  </button>

                  <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>
                    <strong style={{ color: 'var(--text)' }}>{/* no key needed for "Instructions:" */}</strong>
                    {t('mapEditor.instructions')}
                  </div>

                  <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                    {t('mapEditor.currentGrid')} <strong style={{ color: 'var(--text)' }}>{width}×{height}</strong><br />
                    ({width * height} {t('maps.cellsUnit')})
                  </div>
                </div>

                {/* Grid */}
                <div className="mapEditorPanel" style={{ overflow: 'auto' }}>
                  <div
                    className="mapGrid"
                    style={{ gridTemplateColumns: `repeat(${width}, 28px)` }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {cells.map((row, y) =>
                      row.map((cell, x) => {
                        const elev = (cell.elevation ?? 0) as ElevationLevel;
                        const isPendingA = climbPendingA?.x === x && climbPendingA?.y === y;
                        const climb = cell.climb_connection;
                        return (
                          <div
                            key={`${x}-${y}`}
                            className="mapCell"
                            data-cell-type={cell.type}
                            title={`(${x}, ${y}) — ${t(`cell.${cell.type}` as Parameters<typeof t>[0])} · ${t(`elevationLevel.${elev}` as Parameters<typeof t>[0])}${climb ? ` · ${t(`climbType.${climb.type}` as Parameters<typeof t>[0])} → (${climb.targetX}, ${climb.targetY})` : ''}`}
                            onMouseDown={() => onCellDown(y, x)}
                            onMouseEnter={() => onCellEnter(y, x)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', userSelect: 'none',
                              position: 'relative',
                              // Preenchimento sólido (não apenas uma borda) — o piso elevado
                              // precisa parecer um bloco maciço, nunca um "buraco" vazio.
                              background: cell.floor === 'dirt' && elev === 0 ? DIRT_COLOR : ELEVATION_FILL[elev],
                              boxShadow: isPendingA
                                ? 'inset 0 0 0 2px #ffe680'
                                : elev !== 0 ? `inset 0 0 0 1px ${ELEVATION_SHADE[elev]}` : undefined,
                            }}
                          >
                            {cell.type === 'Mountain' && '⛰️'}
                            {cell.type === 'Tree' && '🌲'}
                            {cell.type === 'Rock' && '🪨'}
                            {cell.type === 'Lake' && '💧'}
                            {cell.type === 'Full_Obstacle' && '🕳️'}
                            {cell.type === 'Vision_Obstacle' && '🌫️'}
                            {elev !== 0 && (
                              <span style={{
                                position: 'absolute', bottom: 0, right: 1, fontSize: 8, fontWeight: 700,
                                color: elev === 2 ? '#fff' : elev < 0 ? '#8ecfff' : '#e8c07a',
                                textShadow: '0 0 2px #000, 0 0 2px #000',
                                lineHeight: 1,
                              }}>
                                {elev}
                              </span>
                            )}
                            {climb && (
                              <span style={{ position: 'absolute', top: 0, left: 1, fontSize: 9, lineHeight: 1, textShadow: '0 0 2px #000, 0 0 2px #000' }}>
                                {CLIMB_ICONS[climb.type]}
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
          
          {/* ── Preview Isométrico, abaixo do editor de mapa. A antiga
                "Guia de Terrenos" (card fixo e verboso) foi substituída pelo
                ícone "?" no painel Modo de Pincel, acima, liberando espaço
                para o preview. ── */}
          <div className="mapEditorPreviewRow">
            <div className="adminCard mapEditorPreviewCard">
              <div className="adminCardHeader">Preview Isométrico</div>
              <div className="mapEditorPreviewBody">
                <CombatGrid
                  map={{
                    width,
                    height,
                    cells: cells.map((row, y) => row.map((cell, x) => ({
                      x, y, type: cell.type, elevation: cell.elevation ?? 0,
                      climb_connection: cell.climb_connection ?? null,
                      floor: cell.floor,
                    }))),
                    background_theme: backgroundTheme,
                    weather_type: previewWeatherType,
                    background_enabled: backgroundEnabled,
                    camera_rotation: cameraRotation,
                    north,
                  }}
                  entities={{}}
                  myEntityId={null}
                  highlights={[]}
                  cellHover={null}
                  birdSpawner={null}
                  onCellClick={() => {}}
                  onCellHover={() => {}}
                  onMouseLeave={() => {}}
                  turnSecondsLeft={null}
                />
              </div>
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="formActions">
            <button type="submit" className="btn btnPrimary" disabled={saving}>
              {saving ? t('common.saving') : isEdit ? t('mapEditor.saveChanges') : t('mapEditor.create')}
            </button>
            <button type="button" className="btn btnSecondary"
              onClick={() => navigate('/admin/maps')}>
              {t('common.cancel')}
            </button>
          </div>

        </form>
      </div>
    </>
  );
}
